<script setup lang="ts">
// Feedback about Majlis (2026-10-27): the screenshot the person reviews (and hides parts of), what isn't working and
// what they'd like instead, and the page's details they choose to send. It goes to the organisation's admins only.
// Nothing leaves this browser before Send; the same report is sent once whatever the network does (its key).
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import Icon from "@/components/ui/Icon.vue";
import { useApi } from "@/composables/useApi";
import { useFeedback } from "@/composables/useFeedback";
import { CAPABILITY_WORDS, sendFeedback, type CapabilityKind, type FeedbackKind, type FeedbackReport } from "@/api/productFeedback";
import { pageDetails, type ApiErrorNote } from "@/lib/diagnostics";
import { exportPng, type Area } from "./capture";
import HideAreas from "./HideAreas.vue";
import { useWorkspaceStore } from "@/stores/workspace";

const { open, retaking, shot, captureFailed, where, draft, retake, takeAgain, keepShot, close } = useFeedback();
const { client } = useApi();
const router = useRouter();
const workspace = useWorkspaceStore();
const dialog = ref<HTMLDialogElement | null>(null);
const hider = ref<InstanceType<typeof HideAreas> | null>(null);
const areas = ref<Area[]>([]), keepImage = ref(true);
const sending = ref(false), error = ref(""), sent = ref<FeedbackReport | null>(null), lost = ref(false);
const details = ref<Record<string, unknown>>({}), send = ref({ device: true, errors: true });
let before: HTMLElement | null = null;

const KINDS: { value: FeedbackKind; label: string; hint: string }[] = [
  { value: "bug", label: "Bug", hint: "something doesn't work" }, { value: "idea", label: "Idea", hint: "something could be better" },
  { value: "question", label: "Question", hint: "something isn't clear" },
  { value: "request", label: "Missing", hint: "Majlis can't do it yet" }];   // 2026-10-28: a request to Majlis's team
const CAPABILITIES = Object.entries(CAPABILITY_WORDS) as [CapabilityKind, string][];
const whatLabel = computed(() => ({ question: "What would you like to know?", idea: "What isn't working well for you?",
  request: "What can't Majlis do yet?" } as Record<string, string>)[draft.kind] ?? "What isn't working for you?");
const errorsSeen = computed(() => (details.value.errors as ApiErrorNote[] | undefined) ?? []);
const hasShot = computed(() => !!shot.value && keepImage.value);

watch(shot, () => { areas.value = []; });   // a new picture: its own hidden areas
watch(open, async (isOpen) => {
  const d = dialog.value;
  if (!d) return;
  if (isOpen) {
    if (!retaking.value && !draft.what) { areas.value = []; keepImage.value = true; sent.value = null; error.value = ""; lost.value = false; }
    details.value = pageDetails(where.screen);
    if (!d.open) { before = document.activeElement as HTMLElement | null; if (typeof d.showModal === "function") d.showModal(); else d.setAttribute("open", ""); }
    await nextTick();
    hider.value?.draw();
    d.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  } else if (d.open) {
    if (typeof d.close === "function") d.close(); else d.removeAttribute("open");
    if (!retaking.value) { before?.focus?.(); before = null; }
  }
});
function chosenDetails(): Record<string, unknown> {
  const d = { ...details.value };
  if (!send.value.device) for (const k of ["browser", "os", "width", "height", "dpr", "theme", "language", "reduced_motion"]) delete d[k];
  if (!send.value.errors) delete d.errors;
  return JSON.parse(JSON.stringify(d)) as Record<string, unknown>;   // plain data, never the page's reactive state
}

async function submit() {
  if (sending.value || !draft.what.trim()) return;
  sending.value = true; error.value = ""; lost.value = false;
  try {
    const image = hasShot.value ? await exportPng(shot.value!, areas.value) : null;
    sent.value = await sendFeedback(client, {
      kind: draft.kind, what: draft.what.trim(), ...(draft.instead.trim() ? { instead: draft.instead.trim() } : {}),
      ...(draft.kind === "request" ? { capability: { kind: draft.capability, product: draft.product.trim() || null, source: "console" as const } } : {}),
      page: where.screen, route: where.route, ...(workspace.current?.key ? { workspace: workspace.current.key } : {}),
      context: chosenDetails(), submission_key: draft.key, ...(image ? { screenshot: image.data } : {}) });
  } catch (e) {
    const err = e as { status?: number; body?: { error?: string }; message?: string };
    if (e instanceof TypeError) {   // the network: it may have arrived; the same key makes it one report either way
      lost.value = true; error.value = "The answer didn't come back. Send again: Majlis makes one report, whatever happened.";
    } else if (err.status) {
      error.value = err.body?.error ? `${err.body.error[0].toUpperCase()}${err.body.error.slice(1)}.` : "Your feedback wasn't sent. Try again.";
    } else {   // before anything left this browser (the picture couldn't be made, say)
      error.value = `Your feedback wasn't sent: ${(err.message ?? "something went wrong here").replace(/\.+$/, "")}. Try again, or send without the screenshot.`;
    }
  } finally { sending.value = false; }
}

function done(view: boolean) {
  const id = sent.value?.id;
  close();
  if (view && id) void router.push(`/feedback/${id}`);
}
</script>

<template>
  <div v-if="retaking" class="retake" data-feedback-ui role="region" aria-label="Retake the screenshot">
    <span>Scroll to what you want to show, then take the screenshot.</span>
    <button type="button" class="btn btn--primary btn--sm" @click="takeAgain">Take the screenshot</button>
    <button v-if="shot" type="button" class="btn btn--sm" @click="keepShot">Keep the one I had</button>
  </div>
  <dialog ref="dialog" class="fb" data-feedback-ui aria-labelledby="fb-title" data-test="feedback-dialog" @cancel.prevent="close">
    <div v-if="open && sent" class="sent" role="status">
      <span class="mark" aria-hidden="true"><Icon name="check" :size="20" /></span>
      <h2 id="fb-title">Feedback sent</h2>
      <p>You can follow it, and any answer, in <strong>My feedback</strong>. Reference <span class="mono">{{ sent.id.slice(0, 8) }}</span>.</p>
      <div class="row">
        <button type="button" class="btn btn--primary" data-autofocus @click="done(false)">Back to where I was</button>
        <button type="button" class="btn" @click="done(true)">View report</button>
      </div>
    </div>
    <form v-else-if="open" class="grid" @submit.prevent="submit">
      <section class="shot" aria-labelledby="fb-shot">
        <h2 id="fb-shot" class="small-h">Screenshot</h2>
        <template v-if="hasShot">
          <p class="hint">Review it before sending: it may show names, conversations or another team's work. Typed fields are already
            hidden; drag on it to hide more.</p>
          <HideAreas ref="hider" v-model="areas" :base="shot!" label="Screenshot of the page">
            <button type="button" class="btn btn--sm btn--ghost" @click="retake">Replace screenshot</button>
            <button type="button" class="btn btn--sm btn--ghost" data-test="remove-shot" @click="keepImage = false">Send without it</button>
          </HideAreas>
        </template>
        <div v-else class="none">
          <Icon name="file" :size="22" />
          <p v-if="captureFailed">Majlis couldn't make a screenshot of this page. Your words still go.</p>
          <p v-else>No screenshot: your words go alone.</p>
          <button type="button" class="btn btn--sm" @click="shot ? (keepImage = true) : retake()">{{ shot ? "Add it back" : "Take a screenshot" }}</button>
        </div>
      </section>

      <section class="form">
        <h2 id="fb-title">Send feedback</h2>
        <p class="audience">{{ draft.kind === "request" ? "Your request goes to your organisation's admins, who keep Majlis's backlog. Other workspace members won't see it."
          : "Your report goes to your organisation's admins. Other workspace members won't see it." }}</p>
        <fieldset class="kinds">
          <legend class="small-h">It's</legend>
          <label v-for="k in KINDS" :key="k.value" class="kind" :class="{ on: draft.kind === k.value }">
            <input v-model="draft.kind" type="radio" name="fb-kind" :value="k.value" />
            <span><strong>{{ k.label }}</strong><small>{{ k.hint }}</small></span>
          </label>
        </fieldset>
        <div v-if="draft.kind === 'request'" class="pair">
          <label class="field-block">It's
            <select v-model="draft.capability" class="field" data-test="capability-kind">
              <option v-for="[k, words] in CAPABILITIES" :key="k" :value="k">{{ words }}</option></select></label>
          <label class="field-block">Which product? <span class="muted small">optional</span>
            <input v-model="draft.product" class="field" maxlength="60" placeholder="Grafana" data-test="capability-product" /></label>
        </div>
        <label class="field-block">{{ whatLabel }}
          <textarea v-model="draft.what" class="field" rows="4" maxlength="4000" required data-autofocus aria-required="true" /></label>
        <label class="field-block">{{ draft.kind === "request" ? "What would it let you do?" : "What would you like instead?" }} <span class="muted small">optional</span>
          <textarea v-model="draft.instead" class="field" rows="3" maxlength="4000" /></label>
        <details class="details">
          <summary>Page details <span class="muted small">what helps us reproduce it</span></summary>
          <p class="small">Page: <strong>{{ where.screen || "this page" }}</strong> <span class="mono muted">{{ where.route }}</span> ·
            build <span class="mono">{{ details.version }}</span></p>
          <label class="check"><input v-model="send.device" type="checkbox" />
            <span>Browser and screen: {{ details.browser }} on {{ details.os }}, {{ details.width }}×{{ details.height }}, {{ details.theme }}</span></label>
          <label class="check"><input v-model="send.errors" type="checkbox" :disabled="!errorsSeen.length" />
            <span>{{ errorsSeen.length ? `${errorsSeen.length} recent error${errorsSeen.length === 1 ? "" : "s"} from Majlis (status and request id, never their content)` : "No recent errors" }}</span></label>
          <ul v-if="errorsSeen.length && send.errors" class="errors small mono">
            <li v-for="(e, i) in errorsSeen" :key="i">{{ e.method }} {{ e.route }} → {{ e.status }}</li>
          </ul>
        </details>
        <p v-if="error" class="err small" role="alert">{{ error }}</p>
        <div class="row actions">
          <button type="submit" class="btn btn--primary" :disabled="sending || !draft.what.trim()">{{ sending ? "Sending…" : lost ? "Send again" : "Send feedback" }}</button>
          <button type="button" class="btn" :disabled="sending" @click="close">Cancel</button>
        </div>
      </section>
    </form>
  </dialog>
</template>

<style scoped>
.fb { width: min(1080px, calc(100vw - 32px)); max-width: none; max-height: calc(100vh - 48px); margin: auto; padding: 0; border: 1px solid var(--line);
  border-radius: var(--radius-m); background: var(--bg-elev); color: var(--ink); box-shadow: var(--shadow-3); overflow: hidden; }
.fb[open] { animation: rise-in var(--speed-med) var(--ease-out); }
.fb::backdrop { background: rgb(16 17 20 / 55%); backdrop-filter: blur(2px); }
.grid { display: grid; grid-template-columns: minmax(0, 1fr) 400px; min-height: 0; }
.shot { padding: var(--space-5); border-right: 1px solid var(--line); background: var(--bg-sunk); display: flex; flex-direction: column; gap: var(--space-3); min-width: 0; }
.form { padding: var(--space-5); display: flex; flex-direction: column; gap: var(--space-4); overflow-y: auto; max-height: calc(100vh - 50px); }
.form h2 { margin: 0; font-size: var(--text-xl); }
.small-h { margin: 0; font-size: var(--text-xs); text-transform: uppercase; letter-spacing: .06em; color: var(--muted); }
.hint, .audience { margin: 0; font-size: var(--text-xs); color: var(--muted); line-height: 1.5; }
.audience { padding: var(--space-2) var(--space-3); border-left: 3px solid var(--brand); background: var(--brand-soft); color: var(--ink); border-radius: 0 var(--radius-s) var(--radius-s) 0; }
.none { flex: 1; display: grid; place-content: center; justify-items: center; gap: var(--space-2); text-align: center; color: var(--muted); min-height: 240px; }
.kinds { border: 0; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--space-2); }
.pair { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--space-3); }
.kinds legend { margin-bottom: var(--space-2); }
.kind { display: flex; gap: var(--space-2); align-items: flex-start; padding: var(--space-2) var(--space-3); border: 1px solid var(--line); border-radius: var(--radius-s); cursor: pointer; }
.kind span { display: grid; gap: 2px; }
.kind small { color: var(--muted); font-size: var(--text-xs); }
.kind.on { border-color: var(--brand); background: var(--brand-soft); }
.kind input { margin-top: 3px; }
.field-block { display: grid; gap: var(--space-2); font-size: var(--text-sm); font-weight: 500; }
.field { width: 100%; resize: vertical; }
.details { font-size: var(--text-sm); }
.details summary { cursor: pointer; }
.details > * + * { margin-top: var(--space-2); }
.check { display: flex; gap: var(--space-2); align-items: flex-start; font-size: var(--text-xs); }
.errors { margin: 0; padding-left: var(--space-5); color: var(--muted); max-height: 120px; overflow: auto; }
.row { display: flex; gap: var(--space-2); flex-wrap: wrap; }
.actions { margin-top: auto; }
.err { color: var(--failed); margin: 0; }
.muted { color: var(--muted); font-weight: 400; }
.small { font-size: var(--text-xs); }
.linkish { background: none; border: 0; padding: 0; color: var(--brand); cursor: pointer; font-size: inherit; }
.sent { padding: var(--space-6, 32px); display: grid; gap: var(--space-3); justify-items: start; max-width: 520px; }
.sent h2 { margin: 0; }
.sent p { margin: 0; color: var(--muted); }
.mark { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 50%; background: var(--brand-soft); color: var(--brand); }
.retake { position: fixed; z-index: 90; left: 50%; bottom: var(--space-5); transform: translateX(-50%); display: flex; align-items: center; gap: var(--space-3);
  padding: var(--space-3) var(--space-4); border: 1px solid var(--line); border-radius: var(--radius-m); background: var(--bg-elev);
  box-shadow: 0 12px 32px rgb(0 0 0 / 0.25); font-size: var(--text-sm); max-width: calc(100vw - 32px); flex-wrap: wrap; }
@media (max-width: 860px) {
  .fb { width: 100vw; max-height: 100dvh; height: 100dvh; border-radius: 0; margin: 0; }
  .grid { grid-template-columns: minmax(0, 1fr); }
  .form { order: -1; max-height: none; }
  .shot { border-right: 0; border-top: 1px solid var(--line); }
  .kinds, .pair { grid-template-columns: minmax(0, 1fr); }
}
@media (prefers-reduced-motion: reduce) { .fb[open] { animation: none; } }
</style>
